import { type Dispatch, useMemo, useReducer } from "react";
import { defaultGradient } from "../lib/defaults";
import { makeInterpolatedStop } from "../lib/interpolate";
import { serializeGradient } from "../lib/serialize";
import type {
	Gradient,
	GradientStop,
	LinearGradient,
	RadialGradient,
} from "../lib/types";

export const MIN_STOPS = 2;
export const MAX_STOPS = 6;
const DEFAULT_LINEAR_ANGLE = 90;

/** Discriminated-union action for the gradient editor reducer. */
export type GradientStateAction =
	| { kind: "setType"; value: "linear" | "radial" }
	| { kind: "setAngle"; value: number }
	| { kind: "addStop"; position: number }
	| { kind: "removeStop"; id: string }
	| { kind: "updateStopPosition"; id: string; position: number }
	| { kind: "updateStopColor"; id: string; color: string }
	| { kind: "updateStopOpacity"; id: string; opacity: number }
	| { kind: "selectStop"; id: string }
	| { kind: "reverseStops" }
	| { kind: "swapStopPositions"; idA: string; idB: string };

/** Owned state for the gradient editor. */
export type GradientState = {
	gradient: Gradient;
	/** Always valid — points to a stop that exists in gradient.stops. */
	selectedStopId: string;
	/** Opaque linear angle that survives linear→radial→linear switches. */
	lastLinearAngle: number;
};

export type UseGradientStateReturn = {
	state: GradientState;
	dispatch: Dispatch<GradientStateAction>;
	/** Memoized serializeGradient(state.gradient). */
	css: string;
	canAddStop: boolean;
	canRemoveStop: boolean;
};

const normalizeAngle = (value: number): number => ((value % 360) + 360) % 360;

const clamp = (n: number, lo: number, hi: number): number =>
	Math.max(lo, Math.min(hi, n));

const initState = (initial: Gradient | undefined): GradientState => {
	const gradient = initial ?? defaultGradient();
	return {
		gradient,
		selectedStopId: gradient.stops[0].id,
		lastLinearAngle:
			gradient.type === "linear" ? gradient.angle : DEFAULT_LINEAR_ANGLE,
	};
};

const pickSuccessor = (
	remaining: GradientStop[],
	removedPosition: number,
): string => {
	// Prefer the next stop by position (smallest strictly greater than removed);
	// fall back to the greatest remaining position (the "previous").
	const sorted = [...remaining].sort((a, b) => a.position - b.position);
	const next = sorted.find((s) => s.position > removedPosition);
	if (next) return next.id;
	return sorted[sorted.length - 1].id;
};

const mapStop = (
	stops: GradientStop[],
	id: string,
	patch: (s: GradientStop) => GradientStop,
): GradientStop[] | null => {
	let changed = false;
	const out = stops.map((s) => {
		if (s.id !== id) return s;
		changed = true;
		return patch(s);
	});
	return changed ? out : null;
};

const withStops = (gradient: Gradient, stops: GradientStop[]): Gradient => {
	switch (gradient.type) {
		case "linear":
			return { ...gradient, stops };
		case "radial":
			return { ...gradient, stops };
		default: {
			const _exhaustive: never = gradient;
			void _exhaustive;
			return gradient;
		}
	}
};

const reducer = (
	state: GradientState,
	action: GradientStateAction,
): GradientState => {
	switch (action.kind) {
		case "setType": {
			if (state.gradient.type === action.value) return state;

			// linear → radial
			if (state.gradient.type === "linear" && action.value === "radial") {
				const radial: RadialGradient = {
					type: "radial",
					stops: state.gradient.stops,
				};
				return {
					...state,
					gradient: radial,
					lastLinearAngle: state.gradient.angle,
				};
			}
			// radial → linear: restore stashed angle.
			const linear: LinearGradient = {
				type: "linear",
				angle: state.lastLinearAngle,
				stops: state.gradient.stops,
			};
			return { ...state, gradient: linear };
		}

		case "setAngle": {
			const angle = normalizeAngle(action.value);
			if (state.gradient.type === "linear") {
				if (state.gradient.angle === angle) return state;
				return {
					...state,
					gradient: { ...state.gradient, angle },
					lastLinearAngle: angle,
				};
			}
			if (state.lastLinearAngle === angle) return state;
			return { ...state, lastLinearAngle: angle };
		}

		case "addStop": {
			if (state.gradient.stops.length >= MAX_STOPS) return state;
			// Place the new stop at the requested position (e.g. where the user
			// clicked on the stops track). Its color is interpolated from the two
			// nearest existing stops so it blends seamlessly at that spot.
			const clampedPos = clamp(Math.round(action.position), 0, 100);
			const newStop = {
				...makeInterpolatedStop(state.gradient.stops, clampedPos),
				position: clampedPos,
			};
			const combined = [...state.gradient.stops, newStop].sort(
				(a, b) => a.position - b.position,
			);
			return {
				...state,
				gradient: withStops(state.gradient, combined),
				selectedStopId: newStop.id,
			};
		}

		case "removeStop": {
			if (state.gradient.stops.length <= MIN_STOPS) return state;
			const removed = state.gradient.stops.find((s) => s.id === action.id);
			if (!removed) return state;
			const remaining = state.gradient.stops.filter((s) => s.id !== action.id);
			const nextSelectedId =
				state.selectedStopId === action.id
					? pickSuccessor(remaining, removed.position)
					: state.selectedStopId;
			return {
				...state,
				gradient: withStops(state.gradient, remaining),
				selectedStopId: nextSelectedId,
			};
		}

		case "updateStopPosition": {
			const position = clamp(action.position, 0, 100);
			const stops = mapStop(state.gradient.stops, action.id, (s) => ({
				...s,
				position,
			}));
			if (!stops) return state;
			return { ...state, gradient: withStops(state.gradient, stops) };
		}

		case "updateStopColor": {
			const stops = mapStop(state.gradient.stops, action.id, (s) => ({
				...s,
				color: action.color,
			}));
			if (!stops) return state;
			return { ...state, gradient: withStops(state.gradient, stops) };
		}

		case "updateStopOpacity": {
			const opacity = clamp(action.opacity, 0, 1);
			const stops = mapStop(state.gradient.stops, action.id, (s) => ({
				...s,
				opacity,
			}));
			if (!stops) return state;
			return { ...state, gradient: withStops(state.gradient, stops) };
		}

		case "selectStop": {
			const exists = state.gradient.stops.some((s) => s.id === action.id);
			if (!exists) return state;
			if (state.selectedStopId === action.id) return state;
			return { ...state, selectedStopId: action.id };
		}

		case "reverseStops": {
			const reversed = state.gradient.stops.map((s) => ({
				...s,
				position: 100 - s.position,
			}));
			return { ...state, gradient: withStops(state.gradient, reversed) };
		}

		case "swapStopPositions": {
			const stopA = state.gradient.stops.find((s) => s.id === action.idA);
			const stopB = state.gradient.stops.find((s) => s.id === action.idB);
			if (!stopA || !stopB) return state;
			const swapped = state.gradient.stops.map((s) => {
				if (s.id === action.idA) return { ...s, position: stopB.position };
				if (s.id === action.idB) return { ...s, position: stopA.position };
				return s;
			});
			return { ...state, gradient: withStops(state.gradient, swapped) };
		}

		default: {
			const _exhaustive: never = action;
			void _exhaustive;
			return state;
		}
	}
};

/**
 * Reducer-backed gradient-editor state hook.
 *
 * Owns `gradient`, `selectedStopId`, and `lastLinearAngle` (which survives
 * linear→radial→linear round-trips). Exposes a memoized `css` string plus
 * `canAddStop` / `canRemoveStop` guards for UI affordances.
 *
 * All actions are no-ops when they would violate invariants (min 2 / max 6
 * stops, unknown stop id, type-unchanged, etc.), so callers can dispatch
 * freely without pre-validating.
 */
export const useGradientState = (
	initial?: Gradient,
): UseGradientStateReturn => {
	const [state, dispatch] = useReducer(reducer, initial, initState);
	const css = useMemo(
		() => serializeGradient(state.gradient),
		[state.gradient],
	);
	const stopCount = state.gradient.stops.length;
	const canAddStop = stopCount < MAX_STOPS;
	const canRemoveStop = stopCount > MIN_STOPS;
	return { state, dispatch, css, canAddStop, canRemoveStop };
};
